/**
 * Sets up the client-side logging.
 */
import Axios from 'axios';

import env               from '$/lib/env';
import { stringify }     from '$/lib/utils';
import { LogEvents }     from '$/lib/global';
import { MSTeamsLogger } from '$/lib/MSTeamsLogger';
export { LogEvents, ALaCarteType } from '$/lib/global';
import { BaseLogger, Log, Splitter, Console, Filter, BufferLogger, LoggerLevel } from '$/common/lib/logger';

import { User }     from '$/entities/User';
import { BaseRole } from '$/entities/roles/BaseRole';

export * from '$/common/lib/logger';

/**
 * A logger that POSTs logs to an HTTP endpoint.
 */
class HttpPost extends BaseLogger {

	/**
	 * The URL of the logging API endpoint.
	 */
	url = '';

	constructor(url) {
		super();
		this.url = url;
	}

	log(log: Partial<Log>)
	log(logs: Log[])
	log(error: Error)
	log(level: LoggerLevel, message: string | Error, details: any)
	log(logsOrLevel: Partial<Log> | Partial<Log>[] | Error | LoggerLevel, message?: string | Error, details?: any): Log[] {
		let logs = super.log.apply(this, [ logsOrLevel, message, details ]);

		// commands don't get logged
		logs = logs.filter(log => log.level != LoggerLevel.Command);

		if (typeof navigator.sendBeacon === 'function') {
			let data = '';
			try {
				data = stringify(logs);
			}
			catch (error) {
				data = stringify(error);
			}
			// sendBeacon doesn't block the browser and will send even during unload
			navigator.sendBeacon(this.url, new Blob([ data ], { type : 'application/json' }));
		}
		else {
			void Axios.post(this.url, logs);		// fallback to HTTP post; don't wait for it to finish sending
		}

		return logs;
	}

}

const clientLogger = new Filter(
	// pass-through logger that adds extra details to error logs
	(log: Log) => {
		if (log.level === LoggerLevel.Error || log.level === LoggerLevel.Warn) {
			if (!log.details) {
				log.details = {};
			}
			log.details.url     = window.location.toString();
			log.details.browser = env.browser;
			log.details.os      = env.os;
			log.details.build   = env.build;
		}

		return true;
	},
	new Splitter([
		new Console({ addTimestamp : false }),	// browser console already has timestamps
		new BufferLogger({ maxLength : 50, maxTime : 500, flushMessage : 'FLUSH_BUFFER' },
			new HttpPost('/api/log')
		),
		new Filter(
			(log: Log) => log.message === LogEvents.FeedbackSubmitted,
			new MSTeamsLogger(env.config('teams.LaFeedbackWebhookUrl'), formatLandlordAssistPayload)
		),
	])
);

export default clientLogger as BaseLogger;

/**
 * Allows attaching of other loggers from anywhere within the app.
 */
export function addLogger(logger: BaseLogger) {
	return (clientLogger.truthyLogger as Splitter).addLogger(logger);
}

/**
 * Formats the payload for the LandlordAssist Teams feedback webhook.
 */
function formatLandlordAssistPayload(log: Log): string {
	const message = [ `<b>${env.getEnvironmentPrefixedText('User Feedback')}</b>` ];

	if (!_.isNil(log.details?.rating)) {
		message[0] += log.details.rating === 1 ? ' 👍' : ' 👎';
	}

	if (User.current) {
		message.push(`<b>User:</b> <a href="/users/${BaseRole.current.id}">${User.current.fullName}</a>`);
	}

	if (!_.isNil(log.details?.comment)) {
		message.push(`<b>Comment:</b> ${log.details.comment}`);
	}

	if (!_.isNil(log.details?.threadId)) {
		const threadUrl = env.getAbsoluteUrl(`/landlord-assist/chat/${log.details.threadId}`);
		const url       = !_.isNil(log.details.messageIndex) ? `${threadUrl}?messageIndex=${log.details.messageIndex}` : threadUrl;
		message.push(`<b>Thread:</b> <a href="${url}">View Thread in BES</a>`);
	}

	return message.join('<br>');
}
